import React, { useState, useRef, useEffect, useCallback } from "react";
import lottie from "lottie-web";
import timer10Data from "./assets/timer_10.json";
import timer3Data from "./assets/timer.json";
import { CvWebRtcClient } from "../../../services/vision/CvWebRtcClient";
import { CvProtocolHandler } from "../../../services/vision/CvProtocolHandler";
import { markCv, markCvOnce, resetCvMarks, reportCv } from "../../../services/vision/cvPerfMarks";
import { takeWarmStream, releaseWarmCamera, prewarmCamera } from "../../../services/vision/cameraPrewarm";
import { takeSession, prewarmPendingSession, CV_SESSION_PRECONNECT } from "../../../services/vision/sessionPrewarm";
import { calibrationFields } from "../../../services/vision/cvClientMode";
import { FeedbackMapper } from "./FeedbackMapper";
import { ArrowLeft } from "lucide-react";
import { getReferenceVideo, REFERENCE_PLAYBACK_RATE } from "../../../exercises-data/referenceVideos";
import { speak as ttsSpeak } from "../../../services/tts";

const STALL_MS = 4000;            // тишина после старта — тревога
const PRE_SESSION_STALE_MS = 15000; // фоновая сессия старше (от calibrator / connect) — сервер уже вышел из активного состояния
const FRESH_SILENCE_MS = 4000;      // после авто-ротации: свежая сессия молчит дольше — показываем ошибку
const TRAINER_STALL_MS = 20000;        // тренажёр: прогресса нет — сервер завис
const TRAINER_CHANNEL_ALIVE_MS = 5000; // канал считается живым, если сообщения/pong приходили не позже
const TRAINER_DONE_PAUSE_MS = 2000;    // пауза после последнего повтора, чтобы увидеть результат
const TRAINER_START_HINT_MS = 1000;    // «Начинайте» (текст) — на 1-й секунде окна (требование врача)
const TRAINER_IDLE_ERROR_MS = 3000;    // 3-я секунда окна → инструкция упражнения (в окне 1 — «У вас 10 секунд»)
const TRAINER_LIPS_HINT_MS = 5000;     // 5-я секунда окна → «У вас 10 секунд» (сразу за инструкцией)
const IDLE_HINT_MARGIN_MS = 750;       // если «Начинайте» пришлось бы показать позже 2,25 с — не мигаем: на 3-й секунде идёт «У вас 10 секунд»
const FEEDBACK_MIN_MS = 2500;          // результат повтора (зелёный/красный) висит минимум столько, «Начинайте» его не стирает
const SUCCESS_MIN_CATEGORY = 3;        // как в версии от 6.10: успех = нет кодов ошибок И category_name >= 3
const IDLE_REMINDER_CODE = "8";        // «У вас 10 секунд для повтора»
// Тексты лестницы подсказок — формулировки врача: за окно каждая подсказка
// звучит и пишется РОВНО ОДИН раз, по расписанию 1-й / 3-й / 5-й секунды.
// Кодам лестницы (в том числе тем же кодам из серверных подсказок) отвечаем
// этими текстами, остальным кодам — FeedbackMapper.
const LADDER_TEXTS = {
  "31": "Начинайте",
  "8": "У вас 10 секунд",
  "4": "Растяните губы и улыбнитесь",
  "63": "Растяните губы пошире",
  "201": "Откройте рот и округлите губы, как будто говорите „а-а-а“",
};
// Инструкция «как делать упражнение» (требование врача): звучит в САМОМ
// начале упражнения — в первом окне (звук feedback_<код>.mp3 + текст), а при
// бездействии в окнах 2+ повторяется ПЕРВОЙ, перед «У вас 10 секунд».
// code — имя файла feedback_<код>.mp3 в storage. Номер может быть условным
// (Удивись = 9901, такой код CV-модуль не шлёт): это только имя файла звука.
const INSTRUCTION_BY_SLUG = {
  "smiley-face": { code: "4", text: LADDER_TEXTS["4"] }, // Улыбка
  "round-lips": { code: "9902", text: "Вытяните губы вперёд трубочкой" },   // Слоник
  "kisses": { code: "9903", text: "Вытяните губы вперёд, как для поцелуя" }, // Поцелуйчик
};
const INSTRUCTION_BY_TITLE = {
  "улыбк": { code: "4", text: LADDER_TEXTS["4"] },       // Улыбка
  "заборчик": { code: "63", text: LADDER_TEXTS["63"] },  // Заборчик
  "рот": { code: "201", text: LADDER_TEXTS["201"] },     // Открой рот

  "удив": { code: "9901", text: "Приподнимите и опустите брови" }, // Удивись (условный номер 9901: только имя файла feedback_9901.mp3)
};
const exerciseInstruction = (cfg) => {
  const bySlug = INSTRUCTION_BY_SLUG[String(cfg?.slug || "").toLowerCase()];
  if (bySlug) return bySlug;
  const title = String(cfg?.title || "").toLowerCase();
  for (const [part, instr] of Object.entries(INSTRUCTION_BY_TITLE)) {
    if (title.includes(part)) return instr;
  }
  return null;
};
const INSTR_MARK = "instr"; // маркер текстовой (без кода) инструкции в windowHintsRef

const DEBUG_RAW = true;                // [RAW]-лог сообщений сервера в тренажёре (выключить после отладки)
const DISCONNECT_GRACE_MS = 5000; // disconnected обычно лечится сам — ждём
const PROBE_DEADLINE_MS = 25000;  // 10с бюджета + запас (задержки перед START больше нет)
const AUTO_CAMERA_DELAY_MS = 0; // камера стартует сразу; пауза не нужна
const CV_PREWARM_PASS_STREAM = true; // горячий старт потоком: включится в ШАГЕ 3
const NIHSS_START_AUDIO_URL = "https://intellect.ezspeech.ai/storage/v1/object/public/cortivox//nihss_start.mp3";
const GHOST_FINISH_GUARD_MS = 2000; // finished до START или в первые 2с после — не результат пациента
const DEFAULT_HOLD_MS = 2000;
const HOLD_RING_MS = (timer3Data.op - timer3Data.ip) / timer3Data.fr * 1000;
const INTERRUPT_TEXT = {
  pc_failed: "Соединение с модулем распознавания потеряно.",
  pc_disconnected: "Связь с модулем распознавания пропала.",
  dc_closed: "Канал данных закрыт сервером.",
  no_data: "От модуля распознавания перестали приходить данные.",
  no_finished: "Модуль не прислал результат пробы.",
  connect_failed: "Не удалось подключиться к модулю распознавания.",
  no_ready: "Модуль распознавания не завершил калибровку.",
};

// Сколько ждать отчёт CV-модуля перед закрытием соединения. Старый CV-сервер
// (без report_mode="client") его не пришлёт — тогда закрываемся без него.
const CV_REPORT_TIMEOUT_MS = 3000;

// Калибровка CV-модуля (тренажёр): счётчик Calibrating_* идёт до 240 кадров.
const CALIB_FRAMES = 240;

// ─── Озвучка фидбэков (mp3 в storage, по одному на код FeedbackMapper) ───
// Имя файла = код: feedback_5.mp3, feedback_8.mp3, feedback_31.mp3, …
// Один канал: новый фидбэк глушит предыдущий, звуки не накладываются.
// Файла нет / код неизвестен / браузер не смог — молчим: текст и так на экране.
const FEEDBACK_AUDIO_BASE = "https://intellect.ezspeech.ai/storage/v1/object/public/cortivox//";
let feedbackAudio = null;      // сейчас звучащий фидбэк
let feedbackBusyUntil = 0;     // момент (performance.now), когда можно озвучить следующий

// Врач: фидбэки не должны перебивать друг друга. Пока звучит предыдущий —
// новый НЕ озвучиваем (текст подсказки и так на экране), а после его конца
// держим паузу не меньше секунды.
const MIN_FEEDBACK_GAP_MS = 1000;
const FEEDBACK_UNKNOWN_MS = 3000; // длительность неизвестна (метаданные не пришли) — страхуемся

const stopFeedbackAudio = () => {
  if (feedbackAudio) { feedbackAudio.onended = null; feedbackAudio.onerror = null; feedbackAudio.pause(); feedbackAudio = null; }
  feedbackBusyUntil = 0;
};

const playFeedbackAudio = (code) => {
  const key = String(code || "");
  if (!key) return false;
  const now = performance.now();
  if (now < feedbackBusyUntil) {
    console.log(`[AG] фидбэк ${key} не озвучен: предыдущий ещё звучит, до паузы ~${(Math.ceil((feedbackBusyUntil - now) / 100) / 10).toFixed(1)} с`);
    return false;
  }
  // «Начинайте» до переименования лежит в storage под старым именем:
  // для кода 31 пробуем feedback_31.mp3, при неудаче — старый nihss_start.mp3.
  const urls = key === "31"
    ? [`${FEEDBACK_AUDIO_BASE}feedback_31.mp3`, NIHSS_START_AUDIO_URL]
    : [`${FEEDBACK_AUDIO_BASE}feedback_${key}.mp3`];
  let attempt = 0;
  const tryNext = () => {
    if (attempt >= urls.length) return;
    const url = urls[attempt++];
    try {
      const audio = new Audio(url);
      feedbackAudio = audio;
      const busy = (ms) => { if (feedbackAudio === audio) feedbackBusyUntil = performance.now() + ms; };
      busy(FEEDBACK_UNKNOWN_MS); // страховка до загрузки метаданных
      audio.onloadedmetadata = () => busy((Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 3) * 1000 + MIN_FEEDBACK_GAP_MS);
      audio.onended = () => { feedbackBusyUntil = performance.now() + MIN_FEEDBACK_GAP_MS; if (feedbackAudio === audio) feedbackAudio = null; };
      audio.onerror = () => { if (feedbackAudio === audio) { feedbackAudio = null; feedbackBusyUntil = 0; } tryNext(); };
      audio.play().catch((err) => {
        console.warn(`[AG] озвучка недоступна (${url}):`, err?.name);
        if (feedbackAudio === audio) { feedbackAudio = null; feedbackBusyUntil = 0; }
        tryNext();
      });
    } catch (e) { tryNext(); }
  };
  tryNext();
  return true;
};

// Звук «Начинайте»: в шкале (один раз) и в тренажёре (в начале КАЖДОГО повтора).
// Это озвучка кода 31 — та же дорожка feedback_31.mp3, что и у подсказки «Начинай».
const playStartCue = () => playFeedbackAudio("31");

// Активные коды из local_errors: значение не "0" (как FeedbackMapper.checkLocalErrors).
const getActiveCodes = (localErrors) => {
  const out = [];
  if (!Array.isArray(localErrors)) return out;
  for (const err of localErrors) {
    if (err && typeof err === "object") {
      for (const [code, cat] of Object.entries(err)) if (String(cat) !== "0") out.push(String(code));
    } else if (err !== undefined && err !== null) {
      out.push(String(err));
    }
  }
  return out;
};

// Текст подсказки: кодам лестницы — слово врача, остальным кодам — FeedbackMapper.
const hintTextForCodes = (codes) => {
  const texts = codes.map((c) => LADDER_TEXTS[String(c)]);
  return texts.every(Boolean) ? texts.join(", ") : FeedbackMapper.getErrorText(codes);
};

// ── Буфер логов сессии: кнопка «Сохранить лог» отдаёт его файлом. ──
// Перехватываем console.log/warn/error: строки с нашими метками
// ([AG], [RAW], [CV, «СЕРВЕР ПРИСЛАЛ») складываем в window.__cvLog,
// ничего в самих логах не меняя. Обновление страницы консоль чистит —
// буфер переживает её до перезагрузки вкладки.
const CV_LOG_MARKERS = ["[AG]", "[RAW]", "[CV", "СЕРВЕР ПРИСЛАЛ"];
if (typeof window !== "undefined" && !window.__cvLogInstalled) {
  window.__cvLogInstalled = true;
  window.__cvLog = [];
  const pushLine = (level, args) => {
    try {
      let line = args.map((a) => {
        if (typeof a === "string") return a;
        try { return JSON.stringify(a); } catch (e2) { return String(a); }
      }).join(" ");
      if (!CV_LOG_MARKERS.some((p) => line.includes(p))) return;
      // kp-кадры приходят ~10 раз в секунду по тысячи символов — они вымывали
      // из буфера начало сессии (в логе врача оставались последние ~30 с).
      if (line.includes('"kp":[')) line = line.slice(0, 50) + " …[kp-кадр обрезан]";
      const stamped = `${new Date().toISOString().slice(11, 23)} ${level} ${line}`.slice(0, 4000);
      window.__cvLog.push(stamped);
      if (window.__cvLog.length > 4000) window.__cvLog.splice(0, window.__cvLog.length - 4000);
      // Страховка: копия буфера в localStorage раз в 3 с. Если автоскачивание
      // заблокировал браузер, лог достаётся в консоли: copy(localStorage.getItem("cvLogLast"))
      const nowMs = Date.now();
      if (nowMs - (window.__cvLogSavedAt || 0) > 3000) {
        window.__cvLogSavedAt = nowMs;
        try { localStorage.setItem("cvLogLast", window.__cvLog.join("\n")); } catch (e3) { /* ignore */ }
      }
    } catch (e2) { /* логирование не должно ломать игру */ }
  };
  for (const lvl of ["log", "warn", "error"]) {
    const orig = console[lvl].bind(console);
    console[lvl] = (...args) => { pushLine(lvl.toUpperCase(), args); orig(...args); };
  }
}
const downloadCvLog = () => {
  try {
    const text = (window.__cvLog || []).join("\n") || "лог пуст";
    try { localStorage.setItem("cvLogLast", text); } catch (e) { /* ignore */ }
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `cv-log-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  } catch (e) { /* ignore */ }
};

// 0.8 → «0,8», 2 → «2»
const formatSeconds = (sec) => String(Number(sec.toFixed(2))).replace(".", ",");

/**
 * cvInputs — входные данные CV-сессии из прошлых отчётов ({userSettings,
 * calibrationInfo}, GET /api/cv-reports/inputs). Отчёт тренажёра сохраняет
 * сам CV-сервер (сервер-сервер, по user_id из offer), браузер его только
 * показывает. В шкале отчёт кладётся в запись пробы (cvReport) и сохраняется
 * вместе со шкалой.
 */
export default function ArticulationGame({
  exerciseConfig, userId, onComplete, mode = "trainer",
  onChoiceClick, question, cvInputs = null, prewarmAfterClose = false,
}) {
  const [status, setStatus] = useState("Инициализация");
  const [score, setScore] = useState(0);
  const [rep, setRep] = useState(0);
  const [feedback, setFeedback] = useState({ type: "", text: "Ожидание...", errors: [] });
  const [isCameraStarted, setIsCameraStarted] = useState(false);
  const [cameraReady, setCameraReady] = useState(false); // кадр камеры появился
  const [isExerciseStarted, setIsExerciseStarted] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(10);
  const [isHolding, setIsHolding] = useState(false);
  const [holdMs, setHoldMs] = useState(DEFAULT_HOLD_MS);
  const [exiting, setExiting] = useState(false); // «Выйти»: ждём отчёт от CV-сервера
  const [serverStalled, setServerStalled] = useState(false); // тренажёр: сервер не продвигает упражнение
  const [holdSeconds, setHoldSeconds] = useState(null);      // цель удержания от сервера (персональная), секунды

  const videoRef = useRef(null);
  const cvReportReceivedRef = useRef(false); // отчёт этой сессии уже собран CV-сервером (он сам его и сохраняет)
  const finishProbeRef = useRef(null); // как interruptRef: handleMessage мемоизирован
  const canvasRef = useRef(null);
  const clientRef = useRef(null);
  const protocolRef = useRef(null);
  const generationRef = useRef(0);
  const timer10Ref = useRef(null);
  const timer3Ref = useRef(null);
  const anim10Ref = useRef(null);
  const anim3Ref = useRef(null);
  const isHoldingRef = useRef(false);
  const lastFinishedIdRef = useRef("");
  const secondsLeftRef = useRef(10);
  const answeredRef = useRef(false);
  const awaitingTimerRef = useRef(false);  // START ушёл, ждём первый тик таймера
  const holdMsRef = useRef(DEFAULT_HOLD_MS);
  const startExerciseRef = useRef(null);
  const referenceVideoRef = useRef(null);
  const [interrupted, setInterrupted] = useState(null); // null | { reason }
  const interruptedRef = useRef(false);
  const interruptRef = useRef(null);
  const lastMsgAtRef = useRef(0);
  const watchStallRef = useRef(false);
  const startRequestedRef = useRef(false);
  const disconnectTimerRef = useRef(null);
  const deadlineTimerRef = useRef(null);
  const readyTimerRef = useRef(null);
  const autoStartTimerRef = useRef(null);   // автокамера: задержка старта
  const connectingRef = useRef(false);   // замок от повторного вызова камеры
  const autoStartedRef = useRef(false);  // автокамера — один раз на монтирование
  const startSentAtRef = useRef(0);      // момент, когда START реально ушёл в канал
  const lastPercentRef = useRef(0);      // лог сбросов percent для отчёта владельцу CV
  const instructionAudioRef = useRef(null); // <audio> инструкции, если звучит сейчас
  const instructionGuardRef = useRef(null); // страховка: инструкция зависла — стартуем всё равно
  const autoRotatedRef = useRef(false);   // авто-ротация no_ready уже была (одна на пробу; сбрасывает «Повторить»)
  const rotatingRef = useRef(false);      // следующий handleStartCamera показывает «Подключаемся…»
  const rotateRef = useRef(null);         // как interruptRef: таймеры живут в старых замыканиях
  const freshSilenceRef = useRef(null);   // интервал «свежая сессия молчит 4 с»
  // ── тренажёр ──
  const repVerdictsRef = useRef(new Map());   // rep_id → "success" | "fail" (успех «липкий»)
  const maxRepRef = useRef(0);                // счётчик повторов = номер текущего окна (двигается в начале окна)
  const trainerDoneRef = useRef(false);       // цель достигнута, идёт завершение
  const trainerStartedRef = useRef(false);    // упражнение начато (START ушёл)
  const lastProgressAtRef = useRef(0);        // последний сдвиг: смена percent или новый результат
  const stalledRef = useRef(false);
  const holdFromMsRef = useRef(false);        // персональная цель удержания (target_time_hold, мс) уже получена
  const finishTrainerRef = useRef(null);      // как finishProbeRef: handleMessage мемоизирован
  const percentHoldRef = useRef(false);       // пауза после успеха: кольцо не двигаем до нового окна
  const serverPercentRef = useRef(0);         // последнее серверное percent_timer_10s
  const windowActiveRef = useRef(false);      // окно (повтор) идёт
  const idleHintTimerRef = useRef(null);      // 2-я ступень лестницы: 3-я секунда окна → «У вас 10 секунд»
  const startTextTimerRef = useRef(null);     // 1-я ступень лестницы: текст «Начинайте» на 1-й секунде окна
  const lipsHintTimerRef = useRef(null);      // 3-я ступень лестницы: 5-я секунда окна → своя подсказка упражнения
  const windowHintsRef = useRef(new Set());   // подсказки, уже прозвучавшие в ТЕКУЩЕМ окне (за окно — один раз)
  const hintWindowRepRef = useRef(0);         // rep_id окна, к которому относится набор windowHintsRef
  const hintAdvancedRepRef = useRef(0);       // rep_id, которым счётчик двинул СЕРВЕР (подсказка раньше начала окна)
  const instructionDoneRef = useRef(false); // инструкция «как делать» уже прозвучала (начало упражнения)
  const feedbackLockUntilRef = useRef(0);     // до какого момента результат нельзя стирать
  const lastHintTextRef = useRef("");         // последний текст ошибки от сервера в этой сессии

  // Эталон подбирается по slug упражнения — он одинаков у тренажёра
  // (gameRegistry) и у вопросов cv_face_action в шкалах.
  const referenceVideo = getReferenceVideo(exerciseConfig.slug);
  const isClinical = mode === "clinical";
  // Цель повторов: индивидуальная из ЛК (userSettings[slug].max_reps — вкладка
  // «Индивидуальные параметры») или из конфига упражнения. То же число уходит
  // в payload как max_reps.
  const targetReps = isClinical
    ? 1
    : (Number(cvInputs?.userSettings?.[exerciseConfig.slug]?.max_reps) || exerciseConfig.maxReps || 10);

  // Ролики актрисы сняты вертикально (1080x1920 = 9:16), а панель камеры
  // горизонтальная 4:3. Размеры считаем от одной величины, чтобы обе
  // панели были РАВНОЙ ВЫСОТЫ: при 4:3 высота = ширина*3/4, значит
  // образцу нужна ширина (ширина камеры*3/4)*9/16. flex-basis задаём в
  // тех же пропорциях — тогда при сжатии ряда высоты остаются равными.
  const cameraWidth = isClinical ? 440 : 640;
  const panelHeight = (cameraWidth * 3) / 4;
  const referenceWidth = Math.round((panelHeight * 9) / 16);

  // Замедляем после загрузки метаданных: до неё playbackRate сбрасывается.
  useEffect(() => {
    const el = referenceVideoRef.current;
    if (!el) return undefined;
    const apply = () => { el.playbackRate = REFERENCE_PLAYBACK_RATE; };
    apply();
    el.addEventListener('loadedmetadata', apply);
    return () => el.removeEventListener('loadedmetadata', apply);
  }, [referenceVideo]);

  const drawKeypoints = useCallback((kp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (canvas.width !== 640) { canvas.width = 640; canvas.height = 480; }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#00FF00";
    kp.forEach((p) => { if (p && p.length >= 2) ctx.fillRect(p[0] - 2, p[1] - 2, 4, 4); });
  }, []);

  const handleMessage = useCallback((data, generation) => {
    if (generation !== generationRef.current) return;

    lastMsgAtRef.current = Date.now();

    console.log("СЕРВЕР ПРИСЛАЛ:", Date.now(), data);
    markCvOnce("server:first_msg");
    if (DEBUG_RAW && !isClinical && !data.kp) { // ДИАГНОСТИКА: одна строка на сообщение сервера
      const t = startSentAtRef.current ? Date.now() - startSentAtRef.current : -1;
      const isTick = data.percent_timer_10s !== undefined || data.timer_3s !== undefined;
      const extra = ("finished" in data)
        ? ` verbal=${JSON.stringify(data.verbal)} text=${JSON.stringify(data.text)}`
        : (isTick ? "" : ` keys=${Object.keys(data).join(",")}`);
      console.log(`[RAW] t=${t} rep=${data.rep_id ?? "-"} fin=${data.finished ?? "-"} cat=${data.category_name ?? "-"} err=${JSON.stringify(data.local_errors ?? null)} pct=${data.percent_timer_10s ?? "-"} t3=${data.timer_3s ?? "-"} t3s=${data.timer_3s_seconds ?? "-"} hold=${data.target_time_hold ?? "-"}${extra}`);
    }

    // ───────────── Помощники ТРЕНАЖЁРА (в шкале не вызываются) ─────────────
    const resetHoldTimer = (why) => { // малый таймер в 0. Цель удержания (holdSeconds) НЕ трогаем
      if (isClinical) return;
      isHoldingRef.current = false;
      if (anim3Ref.current) anim3Ref.current.goToAndStop(0, true);
      setIsHolding(false);
      console.log(`[AG] таймеры сброшены (${why}): малый`);
    };
    const resetBigTimer = () => { // большое кольцо в 0
      if (isClinical) return;
      if (anim10Ref.current) anim10Ref.current.goToAndStop(0, true);
      secondsLeftRef.current = 10;
      setSecondsLeft(10);
      lastPercentRef.current = 0;
      console.log("[AG] таймеры сброшены: большой");
    };
    const clearIdleTimers = () => {
      clearTimeout(idleHintTimerRef.current);
      clearTimeout(lipsHintTimerRef.current);
    };
    // Результат повтора (успех/ошибка): показываем и держим минимум FEEDBACK_MIN_MS —
    // иначе «Начинайте» нового окна стирает его через ~30 мс.
    const showResult = (fb) => {
      clearTimeout(startTextTimerRef.current);
      setFeedback(fb);
      feedbackLockUntilRef.current = Date.now() + FEEDBACK_MIN_MS;
    };
    // ЛЕСТНИЦА БЕЗДЕЙСТВИЯ (очерёдность врача): если пациент не двигается,
    // в окнах 2+ сначала повторяем ИНСТРУКЦИЮ упражнения («что делать»,
    // idleHintTimerRef, 3-я секунда), затем «У вас 10 секунд» (5-я секунда,
    // lipsHintTimerRef). В окне 1 инструкция уже прозвучала в начале
    // упражнения — там сразу «У вас 10 секунд». Оба шага за окно ОДИН раз
    // (windowHintsRef). percent_timer_10s — просто ЧАСЫ окна, по нему
    // активность не определить; удержание (timer_3s), любой код ошибки и
    // вердикт лестницу гасят. Серверные подсказки 31/8 НЕ гасят: они
    // дублируют кий и «10 секунд», дублирование глушит windowHintsRef.
    const armIdleHint = () => {
      if (isClinical) return;
      clearIdleTimers();
      idleHintTimerRef.current = setTimeout(() => {
        if (generation !== generationRef.current) return;
        if (!trainerStartedRef.current || trainerDoneRef.current || !windowActiveRef.current) return;
        if (isHoldingRef.current || percentHoldRef.current || interruptedRef.current) return;
        const instr = exerciseInstruction(exerciseConfig);
        // Окна 2+: сначала инструкция «что делать», «10 секунд» уходит на 5-ю
        // секунду. Окно 1: инструкция уже звучала в начале упражнения — сразу она.
        if (instr && !windowHintsRef.current.has(instr.code || INSTR_MARK)) {
          windowHintsRef.current.add(instr.code || INSTR_MARK);
          setFeedback({ type: "", text: instr.text, errors: [] });
          if (instr.code) playFeedbackAudio(instr.code);
          console.log(`[AG] 3-я секунда окна — инструкция «${instr.text}»${instr.code ? ` (код ${instr.code})` : " (текстом)"}`);
          return;
        }
        windowHintsRef.current.add(IDLE_REMINDER_CODE);
        setFeedback({ type: "", text: LADDER_TEXTS[IDLE_REMINDER_CODE], errors: [] });
        playFeedbackAudio(IDLE_REMINDER_CODE); // «У вас 10 секунд» голосом (feedback_8.mp3)
        console.log("[AG] 3-я секунда окна — «У вас 10 секунд»");
      }, TRAINER_IDLE_ERROR_MS);
    };
    // 2-я ступень лестницы бездействия: 5-я секунда окна — «У вас 10 секунд»,
    // сразу после инструкции 3-й секунды (очерёдность врача).
    const armLipsHint = () => {
      if (isClinical) return;
      clearTimeout(lipsHintTimerRef.current);
      lipsHintTimerRef.current = setTimeout(() => {
        if (generation !== generationRef.current) return;
        if (!trainerStartedRef.current || trainerDoneRef.current || !windowActiveRef.current) return;
        if (isHoldingRef.current || percentHoldRef.current || interruptedRef.current) return;
        if (windowHintsRef.current.has(IDLE_REMINDER_CODE)) return; // уже звучала
        const instr = exerciseInstruction(exerciseConfig);
        if (!instr || !windowHintsRef.current.has(instr.code || INSTR_MARK)) return; // очередь инструкции ещё не наступила
        windowHintsRef.current.add(IDLE_REMINDER_CODE);
        setFeedback({ type: "", text: LADDER_TEXTS[IDLE_REMINDER_CODE], errors: [] });
        playFeedbackAudio(IDLE_REMINDER_CODE);
        console.log("[AG] 5-я секунда окна — «У вас 10 секунд»");
      }, TRAINER_LIPS_HINT_MS);
    };
    // Начало окна (повтора): звук «Начинайте» — один раз в окне, сразу на старте.
    // Текст «Начинайте» — на 1-й секунде, если ещё висит результат прошлого повтора.
    const startWindow = (why) => {
      if (isClinical || trainerDoneRef.current) return;
      windowActiveRef.current = true;
      // Счётчик = номер ТЕКУЩЕГО повтора и двигается в момент начала окна:
      // в «тихих» окнах (сервер ничего не прислал) он больше не замирает.
      // Исключение: серверная подсказка уже двинула счётчик на ЭТО окно (пришла
      // раньше первого тика процентов) — второй раз двигать нельзя (скачок через номер).
      if (hintAdvancedRepRef.current !== maxRepRef.current && maxRepRef.current < targetReps) {
        maxRepRef.current += 1; setRep(maxRepRef.current);
      }
      hintAdvancedRepRef.current = 0; // окно началось — пометка «двинут сервером» больше не нужна
      resetHoldTimer("новое окно");
      // Инструкция «как делать» — в самом начале упражнения, в первом окне
      // (требование врача): звук + текст вместо кия 31 и 1-секундного «Начинайте».
      // «У вас 10 секунд» в этом окне наступит на 3-й секунде (инструкция уже была).
      const instr = exerciseInstruction(exerciseConfig);
      if (instr && !instructionDoneRef.current) {
        instructionDoneRef.current = true;
        windowHintsRef.current = new Set([instr.code || INSTR_MARK, "31"]); // серверную 31 в окне 1 молчим
        if (instr.code) playFeedbackAudio(instr.code);
        setStatus("Упражнение идет...");
        setFeedback({ type: "", text: instr.text, errors: [] });
        feedbackLockUntilRef.current = Date.now() + FEEDBACK_MIN_MS;
        clearTimeout(startTextTimerRef.current);
        armIdleHint();
        armLipsHint();
        console.log(`[AG] начало упражнения — инструкция «${instr.text}»${instr.code ? ` (код ${instr.code})` : " (текстом, без кода)"}`);
        return;
      }
      // Кий «Начинайте» в окне звучит РОВНО ОДИН раз: если серверная подсказка 31
      // уже прозвучала в этом окне (окно началось, пока шла пауза после успеха) —
      // второй раз не даём. Лестница подсказок при этом начинается заново.
      const alreadyCued = hintWindowRepRef.current === maxRepRef.current && windowHintsRef.current.has("31");
      if (!alreadyCued) playStartCue();
      hintWindowRepRef.current = maxRepRef.current;
      windowHintsRef.current = new Set(["31"]); // серверные 31 в этом окне больше не озвучиваем
      clearTimeout(startTextTimerRef.current);
      const showStart = () => {
        if (generation !== generationRef.current || trainerDoneRef.current) return;
        if (!windowActiveRef.current || isHoldingRef.current || percentHoldRef.current) return;
        setStatus("Упражнение идет...");
        setFeedback({ type: "", text: LADDER_TEXTS["31"], errors: [] });
        console.log("[AG] 1-я секунда окна — «Начинайте» (текст)");
      };
      // Текст «Начинайте» — на 1-й секунде окна. Если ещё висит результат прошлого
      // повтора — сразу после его паузы (FEEDBACK_MIN_MS). Но если результат держит
      // экран почти до «У вас 10 секунд» (3 с) — текст не показываем: напоминание
      // придёт сразу за ним, текст не должен промелькивать на полсекунды.
      const wait = Math.max(TRAINER_START_HINT_MS, feedbackLockUntilRef.current - Date.now());
      if (wait + IDLE_HINT_MARGIN_MS <= TRAINER_IDLE_ERROR_MS) {
        startTextTimerRef.current = setTimeout(showStart, wait);
      }
      armIdleHint();
      armLipsHint();
      console.log(`[AG] окно началось (${why}): «Начинайте»${alreadyCued ? " (кий уже звучал серверной подсказкой)" : ""}`);
    };

    if (data.kp) { drawKeypoints(data.kp); return; }

    // Калибровка (только тренажёр: шкала её пропускает). incorrect — лицо не
    // в маске/не прямо, счётчик тогда стоит.
    const calibFrame = isClinical ? undefined : (data.Calibrating_correct ?? data.Calibrating_incorrect);
    if (calibFrame !== undefined) {
      const percent = Math.min(100, Math.round((100 * Number(calibFrame)) / CALIB_FRAMES));
      const hint = data.Calibrating_correct !== undefined
        ? "держите лицо расслабленным"
        : "смотрите прямо в камеру, лицо расслаблено";
      setStatus("Калибровка");
      setFeedback({ type: "", text: `Калибровка ${percent}% — ${hint}`, errors: [] });
      return;
    }

    if (data.percent_timer_10s !== undefined) {
      const percent = Number(data.percent_timer_10s);
      let windowStartedNow = false;

      // Первый тик после START.
      if (awaitingTimerRef.current && percent < 100) {
        awaitingTimerRef.current = false;
        if (isClinical) {
          setStatus("Упражнение идет...");
          setFeedback({ type: "", text: LADDER_TEXTS["31"], errors: [] });
          // «Начинай» — не только текст, но и звук, строго вместе с первым
          // тиком таймера. Звук не проигрался — таймер всё равно идёт.
          playStartCue();
        } else {
          windowStartedNow = true;
          startWindow("старт");
        }
      }

      if (mode === "clinical" && lastPercentRef.current - percent > 20) {
        console.warn("[AG] percent сброшен", lastPercentRef.current, "→", percent,
          "через", startSentAtRef.current ? Date.now() - startSentAtRef.current : "—", "мс после START");
      }

      let suppress = false;
      if (!isClinical) {
        const prev = serverPercentRef.current;
        serverPercentRef.current = percent; // всегда
        // Новое окно: percent упал в 0 или заметно назад (повторные нули в уже нулевом окне не считаем).
        const isReset = percent === 0 ? prev > 0 : percent + 5 < prev;
        if (!windowStartedNow && isReset && trainerStartedRef.current && !trainerDoneRef.current) {
          percentHoldRef.current = false;
          console.log(`[AG] новое окно: percent ${prev}→${percent}`);
          if (maxRepRef.current >= targetReps) {
            finishTrainerRef.current?.(); // окно целевого повтора закончилось (в т.ч. провалом) — завершаем
          } else {
            startWindow("новое окно");
          }
        }
        if (percentHoldRef.current) { // пауза после успеха: старое окно дотикивает, кольцо не двигаем
          lastProgressAtRef.current = Date.now(); // пауза не должна будить watchdog
          suppress = true;
        }
      }

      if (!suppress) {
        if (percent !== lastPercentRef.current) {
          lastProgressAtRef.current = Date.now();
          if (stalledRef.current && !isClinical) { stalledRef.current = false; setServerStalled(false); }
        }
        lastPercentRef.current = percent;
        if (anim10Ref.current) {
          const frame = Math.round((240 * percent) / 100);
          anim10Ref.current.goToAndStop(frame, true);
        }
        const newSec = Math.max(0, Math.ceil(10 * (1 - percent / 100)));
        if (newSec !== secondsLeftRef.current) {
          secondsLeftRef.current = newSec;
          setSecondsLeft(newSec);
        }
      }
    }

    // Тренажёр: сервер сообщил «все повторы выполнены» — завершаем упражнение.
    // Если в том же сообщении есть finished, сначала оценим его (завершение — в конце ветки).
    if (!isClinical && data.all_rep !== undefined && !("finished" in data)) {
      console.log("[AG] сервер сообщил о завершении упражнения (all_rep)");
      finishTrainerRef.current?.();
      return;
    }

    // Сколько держать позу, решает сервер — таймер удержания идёт ровно столько же.
    // В тренажёре цель ПЕРСОНАЛЬНАЯ (600/800/1000… мс, подбирается при первом занятии) и приходит
    // в target_time_hold (мс, точно). timer_3s_seconds округлён до целых секунд — используем его
    // только пока target_time_hold не приходил.
    if (data.target_time_hold !== undefined) {
      const ms = Number(data.target_time_hold);
      if (ms > 0) {
        if (!isClinical && holdMsRef.current !== ms) {
          console.log(`[AG] цель удержания: ${holdMsRef.current} → ${ms} мс (target_time_hold)`);
        }
        holdMsRef.current = ms;
        setHoldMs(ms);
        if (!isClinical) {
          holdFromMsRef.current = true;
          setHoldSeconds(ms / 1000);
        }
      }
    }
    if (!isClinical && data.timer_3s_seconds !== undefined && !holdFromMsRef.current) {
      const sec = Number(data.timer_3s_seconds);
      if (sec > 0) {
        holdMsRef.current = sec * 1000;
        setHoldMs(sec * 1000);
        setHoldSeconds(sec);
      }
    }

    if (data.timer_3s !== undefined) {
      const isHoldingNow = data.timer_3s === true || data.timer_3s === "true";
      if (isHoldingNow && !isHoldingRef.current && !percentHoldRef.current) {
        clearIdleTimers(); // удержание началось — бездействия нет
        clearTimeout(startTextTimerRef.current); // и «Начинай» тоже не нужно
        isHoldingRef.current = true;
        if (anim3Ref.current) {
          anim3Ref.current.setSpeed(HOLD_RING_MS / holdMsRef.current);
          anim3Ref.current.goToAndPlay(0, true);
        }
        setIsHolding(true);
      } else if (isHoldingNow && percentHoldRef.current) {
        console.log("[AG] timer_3s true в паузе после успеха — игнорирую");
      } else if (!isHoldingNow && isHoldingRef.current && !isClinical) {
        resetHoldTimer("релиз"); // тренажёр: поза отпущена — малый таймер в 0
      }
      // В шкале при false ничего не сбрасываем (прежнее поведение).
    }

    if ("finished" in data) {
      // ХОЛОСТОЙ finished: сервер шлёт его сразу после калибровки — ДО того,
      // как наш START реально ушёл (startSentAtRef = 0), либо в первые
      // GHOST_FINISH_GUARD_MS мс после START (цикл, начавшийся до START).
      // Результат пациента раньше не приходит. Правило общее для шкалы и тренажёра.
      {
        const sinceStart = startSentAtRef.current === 0
          ? Number.NEGATIVE_INFINITY
          : Date.now() - startSentAtRef.current;
        if (sinceStart < GHOST_FINISH_GUARD_MS) {
          console.warn("[AG] холостой finished пропущен:", {
            rep_id: data.rep_id,
            system_time: data.system_time,
            category_name: data.category_name,
            sinceStart,
          });
          return;
        }
        if (isClinical) console.log("[AG] finished через", sinceStart, "мс после START");
      }

      const isFinished = FeedbackMapper.isFinished(data.finished);
      const msgErrors = (data.local_errors || []).map((err) => (typeof err === "object" ? String(Object.keys(err)[0]) : String(err)));

      // ═══════════════ ТРЕНАЖЁР ═══════════════
      // Оценка как в рабочей версии от 6.10 и по логам: значение finished (0/1) НЕ различает успех —
      // финал успешного повтора приходит как finished:0, category_name:5, local_errors:[].
      //  • только коды-подсказки (31 «Начинай», 8 «У вас 10 секунд») → подсказка, повтор не оценивается;
      //  • нет активных кодов ошибок И category_name >= 3 → УСПЕХ;
      //  • иначе → ошибка с текстом из FeedbackMapper (например 4 «Растяни губы и улыбнись»).
      // Успех «липкий»: позднее сообщение по тому же rep_id его не отменяет.
      // Счётчик повторов = наибольший rep_id (одно окно сервера = один повтор).
      if (!isClinical) {
        if (trainerDoneRef.current) return;
        const repId = Number(data.rep_id || 0);
        const verdicts = repVerdictsRef.current;
        const known = verdicts.get(repId);
        const category = Number(data.category_name || 0);
        const activeCodes = getActiveCodes(data.local_errors);
        const errorCodes = activeCodes.filter((c) => !FeedbackMapper.isHintOnly([c]));
        const hintCodes = activeCodes.filter((c) => FeedbackMapper.isHintOnly([c]));

        // Подсказка сервера (31 «Начинайте», 8 «У вас 10 секунд») — не оценка,
        // но rep_id в ней несёт НОМЕР ТЕКУЩЕГО окна: двигаем счётчик и по ней,
        // иначе он замирает на «ничейном» окне и потом скачет через несколько.
        if (errorCodes.length === 0 && hintCodes.length > 0) {
          // Подсказки сервера (31, 8) дублируют НАШУ лестницу (1-я и 3-я секунды
          // окна), поэтому лестницу больше не гасим — она идёт по расписанию врача.
          // Каждая подсказка показывается и озвучивается ОДИН раз за окно: сервер
          // в бездействии шлёт код 31 десятки раз подряд, и раньше «Начинайте»
          // звучало заново на каждый такой пакет (жалоба врача: «раз 20 сказал»).
          // В окне 1 серверная «Начинайте» (код 31, finished=1 — окно ещё идёт)
          // МОЛЧИТ: инструкция уже прозвучала в начале упражнения. Конец окна
          // (finished=0, код 8) молчать не может — он двигает счётчик повторов.
          const isWindowEnd = !FeedbackMapper.isFinished(data.finished);
          if (!isWindowEnd && instructionDoneRef.current && hintWindowRepRef.current === 0) {
            console.log(`[AG] серверная подсказка (код ${hintCodes.join(",")}) rep ${repId} в окне 1 — молчу: инструкция уже прозвучала`);
            return;
          }
          if (repId > 0 && repId !== hintWindowRepRef.current) {
            hintWindowRepRef.current = repId;   // окно, начало которого мы пропустили
            windowHintsRef.current = new Set(); // — его подсказки ещё не звучали
          }
          const fresh = hintCodes.filter((c) => !windowHintsRef.current.has(c));
          fresh.forEach((c) => windowHintsRef.current.add(c));
          if (fresh.length > 0) {
            if (repId > maxRepRef.current) {
              maxRepRef.current = repId; setRep(maxRepRef.current);
              hintAdvancedRepRef.current = repId; // счётчик двинут сервером — startWindow его не двигает
            }
            // Держим 2,5 с, как вердикт: следующее сообщение окна не стирает мгновенно.
            showResult({ type: "", text: hintTextForCodes(fresh), errors: [] });
            playFeedbackAudio(fresh[0]); // голосом (feedback_<код>.mp3)
            console.log(`[AG] серверная подсказка (код ${fresh.join(",")}) rep ${repId} — один раз за окно`);
          } else {
            console.log(`[AG] повтор серверной подсказки (код ${hintCodes.join(",")}) rep ${repId} — в этом окне уже звучала, молчу`);
          }
          return;
        }

        maxRepRef.current = Math.max(maxRepRef.current, repId);
        setRep(maxRepRef.current);
        lastProgressAtRef.current = Date.now();

        const isSuccess = errorCodes.length === 0 && category >= SUCCESS_MIN_CATEGORY;

        if (isSuccess) {
          if (known === "success") { console.log(`[AG] дубль успеха rep ${repId} — игнорирую`); return; }
          verdicts.set(repId, "success");
          clearIdleTimers();
          windowActiveRef.current = false;
          setScore((p) => p + 1);
          setStatus("Успех!");
          showResult({ type: "success", text: "✓ Удержано", errors: [], repId });
          console.log(`[AG] success rep ${repId} (cat=${category}), повторов ${maxRepRef.current}/${targetReps}`);
          resetBigTimer();
          resetHoldTimer("успех");
          percentHoldRef.current = true; // ждём новое окно сервера
          if (repId >= targetReps || data.all_rep !== undefined) finishTrainerRef.current?.();
          return;
        }

        // Ошибка (оценённая попытка, finished=1) или замечание (окно кончилось
        // без удержания, finished=0). По уже успешному повтору запоздалые
        // ошибки не переворачивают результат.
        if (known === "success") { console.log(`[AG] ошибка по уже успешному rep ${repId} — игнорирую`); return; }
        if (known === undefined) verdicts.set(repId, "fail");
        clearIdleTimers();
        const mapped = FeedbackMapper.getErrorText(errorCodes);
        // Неизвестный словарю код (как код 5 в тесте №4): показываем сам код,
        // а не пустую строку — врач видит, ЧТО пришло, и может найти его в логе.
        const text = mapped && String(mapped).trim() ? mapped : `Ошибка (код ${errorCodes.join(", ")})`;
        if (errorCodes.length) lastHintTextRef.current = text;
        if (isFinished) {
          // Оценённая попытка: вердикт — красная плашка и статус «Ошибка».
          setStatus("Ошибка");
          showResult({ type: "error", text: `❌ ${text}`, errors: errorCodes, repId });
        } else {
          // Замечание: текст показываем и держим, но статус упражнения не
          // пугаем («Ошибка» — только для оценённых попыток), окно просто
          // кончилось без удержания — дальше придёт новое.
          showResult({ type: "", text: `❌ ${text}`, errors: [] });
        }
        if (errorCodes.length) playFeedbackAudio(errorCodes[0]); // ошибка голосом (feedback_код.mp3)
        console.log(`[AG] ${isFinished ? "ошибка" : "замечание"} rep ${repId}: коды [${errorCodes.join(",")}] cat=${category}, повторов ${maxRepRef.current}/${targetReps}`);
        resetHoldTimer(isFinished ? "ошибка" : "замечание");
        if (data.all_rep !== undefined) finishTrainerRef.current?.();
        return;
      }

      // ═══════════════ ШКАЛА (прежняя логика без изменений) ═══════════════
      // Подсказка («Начинай», код 31) приходит с finished=1 посреди повтора,
      // окно 10 секунд при этом идёт дальше — это не результат пробы.
      if (isFinished && FeedbackMapper.isHintOnly(msgErrors)) {
        setFeedback({ type: "", text: FeedbackMapper.getErrorText(msgErrors), errors: [] });
        return;
      }

      // finished:0 = попытка не удержана → фиксируем not_held и закрываем сессию.
      if (!isFinished) {
        console.warn("[AG] finished: 0 — не удержана: фиксирую not_held и закрываю сессию");
        if (!answeredRef.current) {
          answeredRef.current = true;
          setIsExerciseStarted(false);
          const rawErrors = data.local_errors || [];
          const errorCodes = rawErrors.map((err) => (typeof err === "object" ? String(Object.keys(err)[0]) : String(err)));
          setStatus("Поза не удержана");
          setFeedback({ type: "error", text: "⏱ Поза не удержана — зафиксировано как неполное выполнение.", errors: errorCodes });
          finishProbeRef.current({
            exercise: exerciseConfig.slug,
            category: Number(data.category_name || 0),
            observations: errorCodes,
            repId: Number(data.rep_id || 0),
            outcome: "not_held",
            reason: "attempt_not_finished",
          });
        }
        return;
      }

      const eventId = `${data.rep_id}_${data.finished}_${data.system_time}`;
      if (eventId !== lastFinishedIdRef.current) {
        lastFinishedIdRef.current = eventId;
        // rep_id сервер считает с 1.
        const repId = Number(data.rep_id || 0);
        setRep(repId);

        if ("local_errors" in data || "category_name" in data) {
          const rawErrors = data.local_errors || [];
          const errorCodes = rawErrors.map((err) => (typeof err === "object" ? String(Object.keys(err)[0]) : String(err)));

          const isFail = FeedbackMapper.checkLocalErrors(data["local_errors"]) || Number(data.category_name || 0) < 3;

          if (isFail) {
            setStatus("Ошибка");
            setFeedback({ type: "error", text: "❌ " + FeedbackMapper.getErrorText(errorCodes), errors: errorCodes });
          } else {
            setStatus("Успех!");
            setFeedback({ type: "success", text: `✅ Отлично! Повторение ${repId} засчитано!`, errors: [] });
            setScore((prev) => prev + 1);
          }

          // Сброс 2-секундного таймера и шкал только после оценки!
          isHoldingRef.current = false;
          if (anim3Ref.current) anim3Ref.current.goToAndStop(0, true);
          setIsHolding(false);

          if (!answeredRef.current) {
            answeredRef.current = true;
            setIsExerciseStarted(false);
            // В ШКАЛЕ нет правильно/неправильно: пациент выполняет как
            // может, а задача пробы — зафиксировать, ЧТО он сделал.
            // Интерпретирует это врач, глядя на выписку.
            // На балл это не влияет: серверный скорер NIHSS пункты 4a-4c
            // не считает вовсе, а инлайновые баллы раннера отбрасываются.
            // finishProbe: результат зафиксирован — сессия CV больше не нужна.
            finishProbeRef.current({
              exercise: exerciseConfig.slug,
              category: Number(data.category_name || 0),
              observations: errorCodes,
              repId,
            });
          }
        }
      }
      return;
    }

    if (data.bad_face_angle !== undefined) {
      setFeedback({ type: "error", text: "Смотри в камеру, не отворачивайся", errors: [] });
      return;
    }

    if (data.start_button_activate) {
      markCv("server:ready");
      reportCv(`CV ${exerciseConfig.slug}${isClinical ? " (шкала)" : ""}`);
      setStatus("Готов к старту");
      setFeedback({ type: "", text: mode === "clinical" ? "Приготовься…" : "Нажми «Начать упражнение»", errors: [] });
      setIsExerciseStarted(true);
      // В ШКАЛЕ камера и сервер уже готовы — сейчас ЗВУЧИТ ИНСТРУКЦИЯ
      // (запись из question_bank; при сбое загрузки — синтез), и только
      // после её окончания уходит START. В ТРЕНАЖЁРЕ кнопка «Начать упражнение» остаётся как была.
      clearTimeout(readyTimerRef.current);
      if (mode === "clinical") {
        const url = question?.audioUrl;
        const text = question?.ttsText || question?.ask || exerciseConfig.title;
        let started = false;
        const go = () => {
          if (started) return;
          started = true;
          clearTimeout(instructionGuardRef.current);
          instructionGuardRef.current = null;
          if (instructionAudioRef.current) { instructionAudioRef.current.pause(); instructionAudioRef.current = null; }
          startExerciseRef.current?.();
        };
        // Страховка: аудио «зависло» при загрузке — проба стартует без него.
        instructionGuardRef.current = setTimeout(go, 15000);
        if (url) {
          const audio = new Audio(url);
          instructionAudioRef.current = audio;
          audio.onended = go;
          // Запись не загрузилась — произносим текст синтезом.
          const fallbackToTts = () => ttsSpeak(text, { onEnd: go });
          audio.onerror = fallbackToTts;
          audio.play().catch(fallbackToTts);
        } else if (text) {
          ttsSpeak(text, { onEnd: go });
        } else {
          go();
        }
      }
      return;
    }
  }, [drawKeypoints, mode, onChoiceClick, exerciseConfig.slug, question, targetReps]);

  useEffect(() => {
    if (isCameraStarted) {
      if (timer10Ref.current && !anim10Ref.current) {
        anim10Ref.current = lottie.loadAnimation({ container: timer10Ref.current, renderer: "svg", loop: false, autoplay: false, animationData: timer10Data });
        anim10Ref.current.goToAndStop(0, true);
      }
      if (timer3Ref.current && !anim3Ref.current) {
        anim3Ref.current = lottie.loadAnimation({ container: timer3Ref.current, renderer: "svg", loop: false, autoplay: false, animationData: timer3Data });
        anim3Ref.current.goToAndStop(0, true);
      }
    }
  }, [isCameraStarted]);

  useEffect(() => {
    if (!isClinical || !isCameraStarted) return undefined;
    const id = setInterval(() => {
      if (answeredRef.current || interruptedRef.current || !watchStallRef.current) return;
      if (Date.now() - lastMsgAtRef.current > STALL_MS) interruptRef.current?.("no_data");
    }, 1000);
    return () => clearInterval(id);
  }, [isClinical, isCameraStarted]);

  // Тренажёр: упражнение начато, повторов меньше цели, прогресса нет >20 с
  // при живом канале → сервер завис (баг протокола). Даём выйти. В шкале не работает.
  useEffect(() => {
    if (isClinical || !isCameraStarted) return undefined;
    const id = setInterval(() => {
      if (!trainerStartedRef.current || trainerDoneRef.current || stalledRef.current) return;
      if (maxRepRef.current >= targetReps) return;
      const channelAlive = Date.now() - lastMsgAtRef.current < TRAINER_CHANNEL_ALIVE_MS;
      if (channelAlive && Date.now() - lastProgressAtRef.current > TRAINER_STALL_MS) {
        stalledRef.current = true;
        console.warn("[AG] watchdog: сервер не продвигает упражнение", { reps: maxRepRef.current, targetReps });
        setServerStalled(true);
        setStatus("Сервер не продвигает упражнение");
        setFeedback({ type: "error", text: "Сервер не продвигает упражнение. Можно вернуться к упражнениям.", errors: [] });
      }
    }, 1000);
    return () => clearInterval(id);
  }, [isClinical, isCameraStarted, targetReps]);

  // Clinical: камера включается сама, без кликов (требование ОРИТ). Ровно один раз.
  useEffect(() => {
    if (!isClinical) return undefined;
    const t = setTimeout(() => {
      if (autoStartedRef.current) return;
      autoStartedRef.current = true;
      console.log("[AG] AUTO camera start");
      handleStartCamera();
    }, AUTO_CAMERA_DELAY_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClinical]);

  useEffect(() => {
    return () => {
      generationRef.current++;
      clearTimeout(disconnectTimerRef.current);
      clearTimeout(deadlineTimerRef.current);
      clearTimeout(readyTimerRef.current);
      clearTimeout(autoStartTimerRef.current);
      clearTimeout(instructionGuardRef.current);
      clearTimeout(idleHintTimerRef.current);
      clearTimeout(lipsHintTimerRef.current);
      clearTimeout(startTextTimerRef.current);
      clearInterval(freshSilenceRef.current);
      stopFeedbackAudio(); // уходим со страницы — фидбэки замолкают
      if (instructionAudioRef.current) { instructionAudioRef.current.pause(); instructionAudioRef.current = null; }
      if (protocolRef.current) protocolRef.current.close();
      if (clientRef.current) clientRef.current.close();
      if (anim3Ref.current) anim3Ref.current.destroy();
      if (anim10Ref.current) anim10Ref.current.destroy();
    };
  }, []);

  const handleStartCamera = async () => {
    if (connectingRef.current) { console.warn("[AG] повторный вызов камеры отброшен"); return; }
    connectingRef.current = true;
    setCameraReady(false); // новая проба: камера ещё не готова
    setIsConnecting(true);
    generationRef.current++;
    answeredRef.current = false;
    cvReportReceivedRef.current = false; // новая сессия — новый отчёт

    console.trace("[AG] handleStartCamera called"); // кто вызвал? (диагностика)
    resetCvMarks();
    markCv("camera:start");
    startRequestedRef.current = false;
    startSentAtRef.current = 0; // новая сессия: finished до START снова холостые
    if (canvasRef.current) { // чистим замороженный кадр перед новой пробой
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    lastPercentRef.current = 0;
    watchStallRef.current = false;
    interruptedRef.current = false;
    lastMsgAtRef.current = Date.now();
    setInterrupted(null);

    if (protocolRef.current) { protocolRef.current.close(); protocolRef.current = null; }
    if (clientRef.current) { clientRef.current.close(); clientRef.current = null; }
    isHoldingRef.current = false;
    setIsHolding(false);
    awaitingTimerRef.current = false;
    clearTimeout(idleHintTimerRef.current);
    clearTimeout(lipsHintTimerRef.current);
    clearTimeout(startTextTimerRef.current);
    windowHintsRef.current = new Set();
    hintWindowRepRef.current = 0;
    hintAdvancedRepRef.current = 0;
    instructionDoneRef.current = false; // новая сессия — инструкция заново
    lastFinishedIdRef.current = "";
    repVerdictsRef.current = new Map();
    maxRepRef.current = 0;
    trainerDoneRef.current = false;
    percentHoldRef.current = false;
    serverPercentRef.current = 0;
    windowActiveRef.current = false;
    feedbackLockUntilRef.current = 0;
    lastHintTextRef.current = "";
    holdFromMsRef.current = false;
    if (!isClinical) { holdMsRef.current = DEFAULT_HOLD_MS; setHoldMs(DEFAULT_HOLD_MS); }
    setHoldSeconds(null);
    trainerStartedRef.current = false;
    stalledRef.current = false;
    setServerStalled(false);
    secondsLeftRef.current = 10;
    setRep(0);
    setScore(0);
    setStatus("Подключение к CV-серверу...");
    setFeedback({ type: "", text: "Ожидание ответа от сервера...", errors: [] });

    if (rotatingRef.current) { // авто-ротация: текст выше затёрт — возвращаем
      rotatingRef.current = false;
      setStatus("Подключаемся…");
      setFeedback({ type: "", text: "Подключаемся…", errors: [] });
    }

    // ── ХОД 3: фоновая сессия (session pre-connect). Любой сбой → обычный путь ниже. ──
    if (isClinical && CV_SESSION_PRECONNECT) {
      const preGen = generationRef.current;
      let sess = null;
      try { sess = await takeSession(exerciseConfig.slug, userId ?? null); } catch (e) { sess = null; }
      // Фоновая сессия собрана без cvInputs. Если у пробы они есть — payload другой, не берём.
      const hasInputs = !!cvInputs?.calibrationInfo
        || Object.keys(cvInputs?.userSettings?.[exerciseConfig.slug] || {}).length > 0;
      if (sess && hasInputs) { sess.release(); sess = null; }
      if (sess && sess.sinceMilestoneMs > PRE_SESSION_STALE_MS) {
        console.warn(`[AG] PRE stale: ${sess.calibrated ? "calibrator" : "connect"} ${Math.round(sess.sinceMilestoneMs)}мс назад > ${PRE_SESSION_STALE_MS} — закрываю (byClient), открываю свежую`);
        sess.release(); // kill: dc.close byClient, pc.close, треки прогрева гасятся
        sess = null;    // дальше обычный путь: камера → offer, как «Повторить»
        setStatus("Подключаемся…");
        setFeedback({ type: "", text: "Подключаемся…", errors: [] });
      }
      if (sess && preGen !== generationRef.current) { sess.release(); connectingRef.current = false; return; }
      if (sess) {
        try {
          clientRef.current = sess.client;
          protocolRef.current = sess.protocol;
          // «Трамплины»: сессия сама передаёт нам сообщения — никакие свойства
          // клиента/протокола не перезаписываем.
          sess.hooks.pcState = (state) => {
            if (preGen !== generationRef.current) return;
            if (state === "connected") {
              clearTimeout(disconnectTimerRef.current);
              setFeedback((f) => (f.text.startsWith("Связь") || f.text.startsWith("Соединение")
                ? { type: "", text: "Связь восстановлена", errors: [] } : f));
            } else if (state === "disconnected") {
              setFeedback({ type: "error", text: "Связь нестабильна...", errors: [] });
              clearTimeout(disconnectTimerRef.current);
              disconnectTimerRef.current = setTimeout(() => interruptRef.current?.("pc_disconnected"), 5000);
            } else if (state === "failed") {
              interruptRef.current?.("pc_failed");
            }
          };
          sess.hooks.onMessage = (data) => handleMessage(data, preGen);
          sess.hooks.onClose = () => { if (preGen === generationRef.current) interruptRef.current?.("dc_closed"); };
          sess.hooks.onAlive = () => { lastMsgAtRef.current = Date.now(); };
          sess.hooks.onReport = (report) => { if (preGen === generationRef.current && report) cvReportReceivedRef.current = true; };
          if (videoRef.current) videoRef.current.srcObject = sess.stream;
          markCv("pre:sess:taken");
          markCv("camera:ready");
          markCv("config:sent");
          setCameraReady(true);
          connectingRef.current = false;
          setIsCameraStarted(true);
          setIsConnecting(false);
          setStatus("Камера включена. Ожидание сервера...");
          console.log("[AG] PRE adopt:", sess.ready ? "ready" : `calibrating (кадр ${sess.calibFrame ?? "?"}/240)`);
          if (sess.ready && sess.readyMsg) {
            handleMessage(sess.readyMsg, preGen); // server:ready → озвучка → START
          } else {
            clearTimeout(readyTimerRef.current);
            readyTimerRef.current = setTimeout(() => rotateRef.current?.("no_ready"), sess.calibLeftMs);
          }
          return;
        } catch (e) {
          console.warn("[AG] PRE adopt failed, откат на обычный путь:", e);
          try { sess.release(); } catch (e2) { /* ignore */ }
          clientRef.current = null;
          protocolRef.current = null;
        }
      }
    }

    const gen = generationRef.current;
    clientRef.current = new CvWebRtcClient({
      iceServers: exerciseConfig.iceServers ?? [],
      onConnectionStateChange: (state) => {
        if (gen !== generationRef.current) return;
        if (state === "connected") {
          markCvOnce("pc:connected");
          clearTimeout(disconnectTimerRef.current);
          setFeedback((f) => (f.text.startsWith("Связь") || f.text.startsWith("Соединение")
            ? { type: "", text: "Связь восстановлена", errors: [] } : f));
        } else if (state === "disconnected") {
          setFeedback({ type: "error", text: "Связь нестабильна...", errors: [] });
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = setTimeout(() => interruptRef.current?.("pc_disconnected"), 5000);
        } else if (state === "failed") {
          if (isClinical) { interruptRef.current?.("pc_failed"); return; }
          setFeedback({ type: "error", text: "Соединение потеряно. Нажмите «Включить камеру» ещё раз.", errors: [] });
          setIsConnecting(false);
          setIsCameraStarted(false);
          setStatus("Сбой соединения");
        }
      },
    });

    let warm = null;
    try {
      warm = CV_PREWARM_PASS_STREAM ? takeWarmStream() : null;
      if (!CV_PREWARM_PASS_STREAM) releaseWarmCamera();
      const stream = await clientRef.current.start(warm ? { warmStream: warm } : undefined);
      if (!stream) throw new Error("Не удалось получить доступ к камере.");
      if (warm && stream !== warm) warm.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = stream;
      markCv("camera:ready");
      setCameraReady(true); // поток получен — заглушку убираем

      const myGeneration = generationRef.current;
      protocolRef.current = new CvProtocolHandler({
        client: clientRef.current,
        onMessage: (data) => handleMessage(data, myGeneration),
        onClose: () => { if (myGeneration === generationRef.current) interruptRef.current?.("dc_closed"); },
        onAlive: () => { lastMsgAtRef.current = Date.now(); },
        onReport: (report) => { if (myGeneration === generationRef.current && report) cvReportReceivedRef.current = true; },
      });

      // report_mode "client": CV-модуль не читает и не пишет данные EZSpeech,
      // настройки и калибровку берёт отсюда, а отчёт отдаёт нам
      // (lgp_cv_module docs/client_report_mode.md). user_id ему только для отчёта.
      const savedSettings = cvInputs?.userSettings?.[exerciseConfig.slug] || {};
      let calibration = cvInputs?.calibrationInfo || null;
      // Кнопка «Калибровка» в ЛК (раздел «Мимика») ставит флаг cvRecalibrate:
      // следующая сессия калибруется заново, сохранённая калибровка не отправляется.
      try {
        if (localStorage.getItem("cvRecalibrate") === "1") {
          localStorage.removeItem("cvRecalibrate");
          calibration = null;
          console.log("[AG] флаг cvRecalibrate: калибрую заново — сохранённая не отправляется");
        }
      } catch (e) { /* хранилище недоступно — калибруем как обычно */ }
      // Диагностика для врача: что страница прислала из GET /api/cv-reports/inputs.
      // Строка попадает в файл лога — сразу видно, дошли ли калибровка и настройки,
      // и подобрал ли CV время удержания (target_time_hold).
      const autoHold = Number(cvInputs?.autoSettings?.[exerciseConfig.slug]?.target_time_hold);
      console.log(`[AG] cvInputs: настройки ${Object.keys(savedSettings).length ? "есть" : "нет"}, калибровка ${calibration ? "есть" : "нет"}, авто-удержание ${Number.isFinite(autoHold) && autoHold > 0 ? autoHold + "с" : "нет"} (GET /api/cv-reports/inputs)`);
      // БЕРЕМ НАСТРОЙКИ МАСКИ ИЗ КОНФИГА!
      const payload = {
        report_mode: "client",
        // max_reps: сначала индивидуальное значение из ЛК, потом конфиг упражнения
        user_settings: { [exerciseConfig.slug]: { ...savedSettings, max_reps: isClinical ? 1 : (Number(savedSettings.max_reps) || exerciseConfig.maxReps || 10) } },
        // Калибровка — ОДИН РАЗ на пациента (требование врача): первое занятие
        // тренажёр калибрует (240 кадров), CV-модуль сохраняет её в отчёте,
        // бэкенд кладёт в базу, GET /api/cv-reports/inputs возвращает
        // calibrationInfo — и следующие сессии стартуют сразу с неё
        // (skip_calibration). В шкале — calibrationFields (там один повтор).
        ...(isClinical
          ? calibrationFields(calibration)
          : (calibration ? { skip_calibration: true, calibration_info: calibration } : {})),
        // первое занятие этим упражнением в тренажёре: CV-модуль подбирает
        // персональные пороги и время удержания, они вернутся в отчёте (updated_settings).
        // В шкале — нет.
        // Персонализация порогов: пока CV их не подобрал (personalized считает
        // бэкенд по сохранённым updated_settings). Ручные значения из ЛК
        // (например max_reps) персонализацию НЕ отменяют.
        personalization: !isClinical && cvInputs?.personalized !== true,
        user_id: userId ?? null,
        exercise_id: exerciseConfig.neuralNetId,
        db_exercise_id: exerciseConfig.dbId,
        exercise_slug: exerciseConfig.slug,
        mask_info: exerciseConfig.maskInfo || { "use_mask": 0, "key_mask": "none" },
        lang_id: 1,
        group: null,
        interval_grade: [3, 5],
        current_time: 0,
        max_time: 1800,
        list_words: {},
        video_transform: exerciseConfig.videoTransform || "none",
        cvUrl: exerciseConfig.cvUrl,
      };

      await protocolRef.current.connect(payload);
      markCv("config:sent");
      if (isClinical) { // зависшая калибровка → ротация / «Повторить / Пропустить», а не вечное «Включаем камеру…»
        clearTimeout(readyTimerRef.current);
        readyTimerRef.current = setTimeout(() => rotateRef.current?.("no_ready"), 40000);
        clearInterval(freshSilenceRef.current);
        if (autoRotatedRef.current) { // это уже свежая сессия после ротации: молчит FRESH_SILENCE_MS → ошибка
          lastMsgAtRef.current = Date.now();
          freshSilenceRef.current = setInterval(() => {
            if (answeredRef.current || interruptedRef.current || startRequestedRef.current) { clearInterval(freshSilenceRef.current); return; }
            if (Date.now() - lastMsgAtRef.current > FRESH_SILENCE_MS) { clearInterval(freshSilenceRef.current); interruptRef.current?.("no_ready"); }
          }, 1000);
        }
      }
      connectingRef.current = false;
      setIsCameraStarted(true);
      setIsConnecting(false);
      setStatus("Камера включена. Ожидание сервера...");
    } catch (err) {
      if (warm) warm.getTracks().forEach((t) => t.stop());
      setFeedback({ type: "error", text: "Ошибка запуска: " + err.message, errors: [] });
      connectingRef.current = false; // снимаем замок и при ошибке: «Повторить пробу» должен работать
      if (protocolRef.current) { protocolRef.current.close(); protocolRef.current = null; }
      if (clientRef.current) { clientRef.current.close(); clientRef.current = null; }
      interruptRef.current?.("connect_failed");
      setIsConnecting(false);
    }
  };

  const handleStartExercise = () => {
    if (isClinical && startRequestedRef.current) return; // защита от двойного автостарта
    if (protocolRef.current) {
      if (isClinical) {
        startRequestedRef.current = true;
        clearTimeout(deadlineTimerRef.current);
        deadlineTimerRef.current = setTimeout(() => interruptRef.current?.("no_finished"), PROBE_DEADLINE_MS);
      }
      const myGeneration = generationRef.current;
      awaitingTimerRef.current = false;
      // "start" уходит сразу, без паузы 6,1 с — и в тренажёре, и в шкале.
      protocolRef.current.sendStart(() => {
        if (myGeneration !== generationRef.current) return;
        awaitingTimerRef.current = true;
        startSentAtRef.current = Date.now();
        if (!isClinical) { trainerStartedRef.current = true; lastProgressAtRef.current = Date.now(); }
        console.log("[AG] clinical START confirmed");
        watchStallRef.current = true;
        lastMsgAtRef.current = Date.now();
      }, 0);
      setIsExerciseStarted(false);
      setStatus("Подготовка...");
      setFeedback({ type: "", text: "Приготовься…", errors: [] });
      if (anim10Ref.current) anim10Ref.current.goToAndStop(0, true);
      secondsLeftRef.current = 10;
      setSecondsLeft(10);
    }
  };

  startExerciseRef.current = handleStartExercise;

  // ХОД 2: камера только что освободилась — греем под следующую пробу (4b/ 4c)
  // или под «Повторить пробу» после сбоя. Тихо в фоне, ошибки не мешают шкале.
  const maybePrewarm = () => { if (prewarmAfterClose) prewarmCamera(); };
  // ХОД 3: окно ~2,5 с после закрытия пробы — стартуем фоновую сессию следующей CV-пробы.
  // Только после завершения/пропуска (не в interrupt: там возможен «Повторить пробу» этой же пробы).
  const maybePrewarmSession = () => { if (prewarmAfterClose) prewarmPendingSession(); };

  // Шкала: забираем отчёт CV-модуля (get_report заодно останавливает
  // упражнение — сервер после finished сам перезапускал бы попытки), кладём
  // его в запись пробы и закрываем соединение. 2 секунды после — пауза,
  // чтобы врач прочёл результат.
  // Пока ждём отчёт, пробу могли прервать, а шкалу — закрыть (оба раза
  // generationRef меняется): тогда запись не отправляем.
  const finishProbe = (record) => {
    const protocol = protocolRef.current;
    const generation = generationRef.current;
    const reportRequest = protocol ? protocol.requestReport(CV_REPORT_TIMEOUT_MS) : Promise.resolve(null);
    reportRequest.then((report) => {
      if (generation !== generationRef.current) return;
      closeConnection();
      maybePrewarm();
      maybePrewarmSession();
      const closedGeneration = generationRef.current;
      const full = report ? { ...record, cvReport: report } : record;
      console.log("[AG] передаю результат в шкалу:", full);
      setTimeout(() => {
        if (closedGeneration === generationRef.current) onChoiceClick(JSON.stringify(full));
      }, 2000);
    });
  };
  finishProbeRef.current = finishProbe;

  // Отчёт тренажёра браузер НЕ сохраняет: CV-модуль сам шлёт его и покадровый
  // trace на POST /api/cv-reports/server (сервер-сервер, по user_id из offer,
  // миграции 009/011/012). Браузер только показывает копию из канала данных.
  // Ранний «Выйти» не теряет отчёт: get_report заставляет CV-сервер собрать
  // и отправить его. Гостю не нужно: без user_id CV-сервер ничего не отправляет.
  const handleExit = async () => {
    if (exiting) return;
    const protocol = protocolRef.current;
    if (protocol && userId && !cvReportReceivedRef.current) {
      setExiting(true);
      const rep = await protocol.requestReport(CV_REPORT_TIMEOUT_MS);
      console.log("[AG] CV REPORT (по запросу):", JSON.stringify(rep));
      if (rep) cvReportReceivedRef.current = true;
    }
    console.log("[AG] сохраняю лог сессии файлом (автоматически)");
    downloadCvLog();
    onComplete();
  };

  // Тренажёр: цель достигнута. Просим отчёт (get_report заодно останавливает сервер, который
  // сам не останавливается), закрываем сессию byClient и возвращаемся в список.
  // Фидбэк последнего повтора остаётся на экране. Гость без user_id отчёт не запрашивает.
  const finishTrainer = async () => {
    if (isClinical || trainerDoneRef.current) return;
    trainerDoneRef.current = true;
    console.log("[AG] цель достигнута — завершаю упражнение");
    clearTimeout(idleHintTimerRef.current);
    clearTimeout(lipsHintTimerRef.current);
    clearTimeout(startTextTimerRef.current);
    setIsExerciseStarted(false);
    setExiting(true);
    const generation = generationRef.current;
    const protocol = protocolRef.current;
    if (protocol && userId && !cvReportReceivedRef.current) {
      const rep = await protocol.requestReport(CV_REPORT_TIMEOUT_MS);
      console.log("[AG] CV REPORT (по запросу):", JSON.stringify(rep));
      if (rep) cvReportReceivedRef.current = true;
    }
    // Лог уходит файлом сам — кнопка «Сохранить лог» больше не обязательна:
    // после последнего повтора страница уходит со списка упражнений, нажать не успевают.
    console.log("[AG] сохраняю лог сессии файлом (автоматически)");
    downloadCvLog();
    if (generation !== generationRef.current) return; // сессию уже закрыли/ушли со страницы
    closeConnection(); // стопит таймеры, release сессии, dc close byClient
    const closedGeneration = generationRef.current;
    setTimeout(() => { if (closedGeneration === generationRef.current) onComplete(); }, TRAINER_DONE_PAUSE_MS);
  };
  finishTrainerRef.current = finishTrainer;

  // Кнопка «Вернуться к упражнениям» при зависшем сервере: закрыть сессию и выйти.
  const handleStalledBack = () => { closeConnection(); onComplete(); };

  const closeConnection = () => {
    // Фиксируем последний кадр камеры на канвасе: после закрытия соединения
    // видео-элемент гаснет в чёрный, а замороженный кадр остаётся на экране
    // до перехода к следующему заданию — врач видит пациента, а не пустоту.
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas && video.videoWidth > 0) {
      if (canvas.width !== 640) { canvas.width = 640; canvas.height = 480; }
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    }
    generationRef.current++;
    stopFeedbackAudio(); // фидбэки не звучат после закрытия сессии
    clearTimeout(disconnectTimerRef.current);
    clearTimeout(deadlineTimerRef.current);
    clearTimeout(readyTimerRef.current);
    clearTimeout(instructionGuardRef.current);
    clearTimeout(idleHintTimerRef.current);
    clearTimeout(lipsHintTimerRef.current);
    clearTimeout(startTextTimerRef.current);
    clearInterval(freshSilenceRef.current);
    if (instructionAudioRef.current) { instructionAudioRef.current.pause(); instructionAudioRef.current = null; }
    watchStallRef.current = false;
    if (protocolRef.current) { protocolRef.current.close(); protocolRef.current = null; }
    if (clientRef.current) { clientRef.current.close(); clientRef.current = null; }
  };

  const interrupt = (reason) => {
    if (!isClinical || answeredRef.current || interruptedRef.current) return;
    interruptedRef.current = true;
    console.warn("[AG] interrupted:", reason);
    closeConnection();
    maybePrewarm();
    isHoldingRef.current = false;
    setIsHolding(false);
    setIsExerciseStarted(false);
    setIsCameraStarted(false);
    setIsConnecting(false);
    setStatus("Проба прервана");
    setInterrupted({ reason });
  };
  interruptRef.current = interrupt;

  // no_ready — одна тихая ротация на свежую сессию, потом обычная ошибка.
  const rotateOrInterrupt = (reason) => {
    if (!isClinical || answeredRef.current || interruptedRef.current || startRequestedRef.current) return;
    if (reason !== "no_ready" || autoRotatedRef.current) { interruptRef.current?.(reason); return; }
    autoRotatedRef.current = true;
    rotatingRef.current = true;
    console.warn("[AG] no_ready: авто-ротация на свежую сессию");
    closeConnection();
    setIsCameraStarted(false);
    setIsExerciseStarted(false);
    handleStartCamera();
  };
  rotateRef.current = rotateOrInterrupt;

  const handleRetry = () => {
    autoRotatedRef.current = false; // ручной «Повторить» даёт новый бюджет ротации
    interruptedRef.current = false;
    setInterrupted(null);
    handleStartCamera(); // жест врача — единственный легитимный реконнект
  };

  const handleSkip = () => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    closeConnection();
    maybePrewarm();
    maybePrewarmSession();
    const reason = interrupted?.reason ?? null;
    setInterrupted(null);
    // «не выполнено» из-за техсбоя — не результат пациента; reason различает
    onChoiceClick(JSON.stringify({
      exercise: exerciseConfig.slug,
      category: 0,
      observations: [],
      repId: 0,
      outcome: "not_performed",
      reason: "technical_interruption",
      interruptReason: reason,
    }));
  };

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px", fontFamily: "sans-serif", width: "100%", maxHeight: "100%", overflowY: "auto" }}>
      {mode === "trainer" && (
        <button onClick={handleExit} disabled={exiting} className="absolute top-4 left-4 flex items-center gap-2 text-gray-600 hover:text-gray-900 z-10 bg-white/70 px-3 py-1.5 rounded-lg">
          <ArrowLeft className="size-5" /> {exiting ? "Сохраняем…" : "Выйти"}
        </button>
      )}

      <h1 style={{ color: "#2E6E62", textAlign: "center", margin: isClinical ? "0 0 4px" : undefined, fontSize: isClinical ? "22px" : undefined }}>{exerciseConfig.title}</h1>

      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", marginTop: "16px" }}>
        {referenceVideo && (
          // Эталон с актрисой — аналог #logopedStream в основном проекте:
          // луп, без звука (инструкцию читает голос), замедленно, чтобы
          // пациент успевал разглядеть артикуляцию.
          <div style={{ position: "relative", flex: `0 1 ${referenceWidth}px`, aspectRatio: "9/16", background: "#000", borderRadius: "8px", overflow: "hidden" }}>
            <video
              ref={referenceVideoRef}
              src={referenceVideo}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <span style={{ position: "absolute", bottom: 8, left: 8, color: "#fff", fontSize: 12, background: "rgba(0,0,0,0.5)", padding: "2px 8px", borderRadius: 6 }}>
              Образец
            </span>
          </div>
        )}

        <div style={{ position: "relative", flex: `0 1 ${cameraWidth}px`, aspectRatio: "4/3", background: "#000", borderRadius: "8px" }}>
          {!cameraReady && isClinical && (
            <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#222", color: "#aaa", fontSize: 18, zIndex: 4, borderRadius: "8px" }}>
              Включаем камеру…
            </div>
          )}
          <video ref={videoRef} autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} />
          <canvas ref={canvasRef} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", transform: "scaleX(-1)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: 0, left: 0, width: "16px", height: "100%", background: "rgba(255,255,255,0.2)" }}>
            {isHolding && <div data-testid="cv-hold-bar" style={{ position: "absolute", bottom: 0, width: "100%", height: "0%", background: "linear-gradient(to top, #dc3545, #ffc107, #28a745)", animation: `hold-anim ${holdMs}ms linear forwards` }}></div>}
          </div>
          <div style={{ position: "absolute", top: 0, right: 0, width: "16px", height: "100%", background: "rgba(255,255,255,0.2)" }}>
            {isHolding && <div data-testid="cv-hold-bar" style={{ position: "absolute", bottom: 0, width: "100%", height: "0%", background: "linear-gradient(to top, #dc3545, #ffc107, #28a745)", animation: `hold-anim ${holdMs}ms linear forwards` }}></div>}
          </div>
        </div>

        <div style={{ flex: "0 0 150px", display: "flex", justifyContent: "center", alignItems: "center", alignSelf: "stretch" }}>
          {isCameraStarted && (
            <div style={{ position: "relative", width: "150px", height: "150px" }}>
              <div ref={timer10Ref} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1 }}></div>
              <div ref={timer3Ref} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 2 }}></div>
              <span style={{ position: "absolute", zIndex: 3, color: "white", fontSize: "40px", fontWeight: "bold", display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }} data-testid="cv-seconds">{secondsLeft}</span>
            </div>
          )}
        </div>
      </div>

      <style>{"@keyframes hold-anim { 0% { height: 0%; } 100% { height: 100%; } }"}</style>

      {interrupted && (
        <div style={{ margin: "16px 0", padding: 16, background: "#fff4e5", borderLeft: "4px solid #f59e0b", borderRadius: 8, textAlign: "left" }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Проба прервана</div>
          <div style={{ marginBottom: 12, fontSize: 14 }}>
            {INTERRUPT_TEXT[interrupted.reason] ?? "Потеряна связь с модулем распознавания."} Это технический сбой, а не результат пациента.
          </div>
          <button onClick={handleRetry} style={{ padding: "10px 16px", marginRight: 8, background: "#2E6E62", color: "white", border: "none", borderRadius: 8 }}>
            Повторить пробу
          </button>
          <button onClick={handleSkip} style={{ padding: "10px 16px", background: "#fff", border: "1px solid #999", borderRadius: 8 }}>
            Пропустить: не выполнено
          </button>
        </div>
      )}

      <div style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>Статус: <span>{status}</span></div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div>Очки: {score} | Повтор: {rep} / {targetReps}{!isClinical && holdSeconds != null ? ` | Удержание: ${formatSeconds(holdSeconds)} с` : ""}</div>
          <button onClick={downloadCvLog} title="Скачать все логи сессии файлом" style={{ padding: "4px 10px", fontSize: 13, background: "#eef2f0", color: "#2E6E62", border: "1px solid #cfd8d4", borderRadius: 6 }}>
            Сохранить лог
          </button>
        </div>
      </div>

      <div style={{ textAlign: "center", margin: "20px 0" }}>
        {!isCameraStarted ? (
          isClinical ? (
            <div style={{ color: "#666", fontSize: 16 }}>Подключаем камеру…</div>
          ) : (
            <button onClick={handleStartCamera} disabled={isConnecting} style={{ padding: "15px", fontSize: "18px", background: isConnecting ? "#ccc" : "#2E6E62", color: "white", border: "none", borderRadius: "8px" }}>
              {isConnecting ? "Подключение..." : "Включить камеру"}
            </button>
          )
        ) : isExerciseStarted && !isClinical && (
          <button onClick={handleStartExercise} style={{ padding: "15px", fontSize: "18px", background: "#2E6E62", color: "white", border: "none", borderRadius: "8px" }}>
            Начать упражнение
          </button>
        )}
      </div>

      <div style={{ padding: "15px", background: feedback.type === "success" ? "#e6f7e6" : feedback.type === "error" ? "#ffe6e6" : "#f9f9f9", borderLeft: `4px solid ${feedback.type === "success" ? "#28a745" : feedback.type === "error" ? "#dc3545" : "#ccc"}` }}>
        <span data-testid="cv-feedback">{feedback.text}</span>
        {feedback.errors.length > 0 && <div style={{ color: "#c92a2a" }}>{feedback.errors.join(", ")}</div>}
        {feedback.repId ? <div style={{ fontSize: 12, color: "#7a869a", marginTop: 2 }}>оценка за повтор {feedback.repId}</div> : null}
        {serverStalled && !isClinical && (
          <button onClick={handleStalledBack} style={{ marginTop: 12, padding: "10px 16px", background: "#2E6E62", color: "white", border: "none", borderRadius: "8px" }}>
            Вернуться к упражнениям
          </button>
        )}
      </div>
    </div>
  );
}